import { useUnityBuildStore } from '../../stores/store';

/**
 * AnnotationMeasurements provides mathematical functions for converting
 * pixel distances to real-world measurements for annotation overlays
 */

// Constants
const PIXEL_RESOLUTION = 72; // Standard DPI for SVG measurements
const INCHES_PER_FOOT = 12;

/**
 * Get the current document scale from the store
 * @returns The current user scale (feet per inch)
 */
export const getCurrentScale = (): number => {
  const store = useUnityBuildStore.getState();
  return store.userScale || 0;
};

/**
 * Convert pixel distance to real-world distance in feet
 * @param pixelDistance - Distance in pixels
 * @param scale - Document scale (feet per inch), defaults to current scale
 * @returns Distance in feet
 */
export const pixelsToFeet = (pixelDistance: number, scale?: number): number => {
  const currentScale = scale ?? getCurrentScale();
  if (currentScale <= 0) return 0;

  // Convert pixels to inches, then to feet using scale
  const inches = pixelDistance / PIXEL_RESOLUTION;
  const feet = inches * currentScale;

  return feet;
};

/**
 * Convert pixel distance to real-world distance in inches
 * @param pixelDistance - Distance in pixels
 * @param scale - Document scale (feet per inch), defaults to current scale
 * @returns Distance in inches
 */
export const pixelsToInches = (pixelDistance: number, scale?: number): number => {
  const currentScale = scale ?? getCurrentScale();
  if (currentScale <= 0) return 0;

  // Convert pixels to inches, then to feet, then back to inches
  const feet = pixelsToFeet(pixelDistance, currentScale);
  const inches = feet * INCHES_PER_FOOT;

  return inches;
};

/**
 * Format a distance in feet to a human-readable string (ft and inches)
 * @param feet - Distance in feet
 * @param precision - Number of decimal places for inches (default: 1)
 * @returns Formatted string like "5 ft 3.2 in" or "0 ft 6.5 in"
 */
export const formatFeetAndInches = (feet: number, precision: number = 1): string => {
  if (feet === 0) return '0 ft 0 in';

  const wholeFeet = Math.floor(Math.abs(feet));
  const remainingInches = (Math.abs(feet) - wholeFeet) * INCHES_PER_FOOT;

  const sign = feet < 0 ? '-' : '';

  if (wholeFeet === 0) {
    return `${sign}${remainingInches.toFixed(precision)} in`;
  }

  if (remainingInches < 0.1) {
    return `${sign}${wholeFeet} ft`;
  }

  return `${sign}${wholeFeet} ft ${remainingInches.toFixed(precision)} in`;
};

/**
 * Format a distance in feet to a compact string (decimal feet)
 * @param feet - Distance in feet
 * @param precision - Number of decimal places (default: 2)
 * @returns Formatted string like "5.25 ft"
 */
export const formatFeet = (feet: number, precision: number = 2): string => {
  if (feet === 0) return '0 ft';

  const sign = feet < 0 ? '-' : '';
  return `${sign}${Math.abs(feet).toFixed(precision)} ft`;
};

/**
 * Calculate the distance between two points in pixels
 * @param point1 - First point {x, y}
 * @param point2 - Second point {x, y}
 * @returns Distance in pixels
 */
export const calculatePixelDistance = (
  point1: { x: number; y: number },
  point2: { x: number; y: number }
): number => {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Calculate the area of a rectangle in square feet
 * @param widthPixels - Width in pixels
 * @param heightPixels - Height in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Area in square feet
 */
export const calculateRectangleArea = (
  widthPixels: number,
  heightPixels: number,
  scale?: number
): number => {
  const widthFeet = pixelsToFeet(widthPixels, scale);
  const heightFeet = pixelsToFeet(heightPixels, scale);
  return widthFeet * heightFeet;
};

/**
 * Calculate the area of a circle in square feet
 * @param radiusPixels - Radius in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Area in square feet
 */
export const calculateCircleArea = (radiusPixels: number, scale?: number): number => {
  const radiusFeet = pixelsToFeet(radiusPixels, scale);
  return Math.PI * radiusFeet * radiusFeet;
};

/**
 * Calculate the area of an ellipse in square feet
 * @param radiusXPixels - X radius in pixels
 * @param radiusYPixels - Y radius in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Area in square feet
 */
export const calculateEllipseArea = (
  radiusXPixels: number,
  radiusYPixels: number,
  scale?: number
): number => {
  const radiusXFeet = pixelsToFeet(radiusXPixels, scale);
  const radiusYFeet = pixelsToFeet(radiusYPixels, scale);
  return Math.PI * radiusXFeet * radiusYFeet;
};

/**
 * Calculate the length of a polyline in feet (open path)
 * @param points - Array of points {x, y} in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Length in feet
 */
export const calculatePolylineLength = (
  points: Array<{ x: number; y: number }>,
  scale?: number
): number => {
  if (points.length < 2) return 0;

  let totalPixelDistance = 0;

  // Calculate distances between consecutive points (no closing segment)
  for (let i = 0; i < points.length - 1; i++) {
    totalPixelDistance += calculatePixelDistance(points[i], points[i + 1]);
  }

  return pixelsToFeet(totalPixelDistance, scale);
};

/**
 * Calculate the perimeter of a polygon in feet
 * @param points - Array of points {x, y} in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Perimeter in feet
 */
export const calculatePolygonPerimeter = (
  points: Array<{ x: number; y: number }>,
  scale?: number
): number => {
  if (points.length < 2) return 0;

  let totalPixelDistance = 0;

  // Calculate distances between consecutive points
  for (let i = 0; i < points.length - 1; i++) {
    totalPixelDistance += calculatePixelDistance(points[i], points[i + 1]);
  }

  // Close the polygon if it's not already closed
  if (points.length > 2) {
    totalPixelDistance += calculatePixelDistance(points[points.length - 1], points[0]);
  }

  return pixelsToFeet(totalPixelDistance, scale);
};

/**
 * Calculate the area of a polygon in square feet using the shoelace formula
 * @param points - Array of points {x, y} in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Area in square feet
 */
export const calculatePolygonArea = (
  points: Array<{ x: number; y: number }>,
  scale?: number
): number => {
  if (points.length < 3) return 0;

  // Convert points to feet first
  const pointsInFeet = points.map(point => ({
    x: pixelsToFeet(point.x, scale),
    y: pixelsToFeet(point.y, scale),
  }));

  // Shoelace formula
  let area = 0;
  for (let i = 0; i < pointsInFeet.length; i++) {
    const j = (i + 1) % pointsInFeet.length;
    area += pointsInFeet[i].x * pointsInFeet[j].y;
    area -= pointsInFeet[j].x * pointsInFeet[i].y;
  }

  return Math.abs(area) / 2;
};

/**
 * Format area in square feet to a human-readable string
 * @param areaSquareFeet - Area in square feet
 * @param precision - Number of decimal places (default: 2)
 * @returns Formatted string like "25.5 sq ft"
 */
export const formatArea = (areaSquareFeet: number, precision: number = 2): string => {
  if (areaSquareFeet === 0) return '0 sq ft';

  const sign = areaSquareFeet < 0 ? '-' : '';
  return `${sign}${Math.abs(areaSquareFeet).toFixed(precision)} sq ft`;
};

/**
 * Calculate the perimeter of a rectangle in feet
 * @param widthPixels - Width in pixels
 * @param heightPixels - Height in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Perimeter in feet
 */
export const calculateRectanglePerimeter = (
  widthPixels: number,
  heightPixels: number,
  scale?: number
): number => {
  const currentScale = scale ?? getCurrentScale();
  if (currentScale <= 0) return 0;

  const perimeterPixels = 2 * (widthPixels + heightPixels);
  return pixelsToFeet(perimeterPixels, currentScale);
};

/**
 * Calculate the circumference of a circle in feet
 * @param radiusPixels - Radius in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Circumference in feet
 */
export const calculateCirclePerimeter = (radiusPixels: number, scale?: number): number => {
  const currentScale = scale ?? getCurrentScale();
  if (currentScale <= 0) return 0;

  const circumferencePixels = 2 * Math.PI * radiusPixels;
  return pixelsToFeet(circumferencePixels, currentScale);
};

/**
 * Calculate the circumference of an ellipse in feet (approximation)
 * @param radiusXPixels - X radius in pixels
 * @param radiusYPixels - Y radius in pixels
 * @param scale - Document scale, defaults to current scale
 * @returns Circumference in feet (approximated using Ramanujan's formula)
 */
export const calculateEllipsePerimeter = (
  radiusXPixels: number,
  radiusYPixels: number,
  scale?: number
): number => {
  const currentScale = scale ?? getCurrentScale();
  if (currentScale <= 0) return 0;

  // Convert to feet first
  const a = pixelsToFeet(radiusXPixels, currentScale);
  const b = pixelsToFeet(radiusYPixels, currentScale);

  // Ramanujan's approximation for ellipse circumference
  const h = Math.pow((a - b) / (a + b), 2);
  const circumference = Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));

  return circumference;
};

/**
 * Get measurement display options based on the current scale
 * @returns Object with measurement preferences
 */
export const getMeasurementOptions = () => {
  const scale = getCurrentScale();

  return {
    scale,
    hasValidScale: scale > 0,
    precision: scale > 0 ? 1 : 0, // Higher precision for valid scales
    unit: 'ft', // Primary unit
    showInches: true, // Always show inches for better precision
  };
};

/**
 * Main function to get formatted measurements for any annotation type
 * @param type - Annotation type
 * @param geometry - Geometry data
 * @param options - Measurement options
 * @returns Object with formatted measurements
 */
export const getAnnotationMeasurements = (
  type: string,
  geometry: any,
  options: { scale?: number; precision?: number } = {}
) => {
  const { scale, precision = 1 } = options;
  const currentScale = scale ?? getCurrentScale();

  if (currentScale <= 0) {
    return {
      width: 'No scale set',
      height: 'No scale set',
      area: 'No scale set',
      length: 'No scale set',
      radius: 'No scale set',
    };
  }

  const measurements: any = {};

  switch (type) {
    case 'rectangle':
    case 'text':
    case 'highlighter': {
      const widthPixels = parseFloat(geometry?.width || '0');
      const heightPixels = parseFloat(geometry?.height || '0');

      measurements.width = formatFeetAndInches(pixelsToFeet(widthPixels, currentScale), precision);
      measurements.height = formatFeetAndInches(
        pixelsToFeet(heightPixels, currentScale),
        precision
      );
      measurements.area = formatArea(
        calculateRectangleArea(widthPixels, heightPixels, currentScale),
        precision
      );
      measurements.perimeter = formatFeetAndInches(
        calculateRectanglePerimeter(widthPixels, heightPixels, currentScale),
        precision
      );
      break;
    }

    case 'circle': {
      const radiusPixels = parseFloat(geometry?.r || '0');

      measurements.radius = formatFeetAndInches(
        pixelsToFeet(radiusPixels, currentScale),
        precision
      );
      measurements.area = formatArea(calculateCircleArea(radiusPixels, currentScale), precision);
      measurements.perimeter = formatFeetAndInches(
        calculateCirclePerimeter(radiusPixels, currentScale),
        precision
      );
      break;
    }

    case 'ellipse': {
      const radiusXPixels = parseFloat(geometry?.rx || '0');
      const radiusYPixels = parseFloat(geometry?.ry || '0');

      measurements.radiusX = formatFeetAndInches(
        pixelsToFeet(radiusXPixels, currentScale),
        precision
      );
      measurements.radiusY = formatFeetAndInches(
        pixelsToFeet(radiusYPixels, currentScale),
        precision
      );
      measurements.area = formatArea(
        calculateEllipseArea(radiusXPixels, radiusYPixels, currentScale),
        precision
      );
      measurements.perimeter = formatFeetAndInches(
        calculateEllipsePerimeter(radiusXPixels, radiusYPixels, currentScale),
        precision
      );
      break;
    }

    case 'line':
    case 'arrow': {
      const x1 = parseFloat(geometry?.x1 || '0');
      const y1 = parseFloat(geometry?.y1 || '0');
      const x2 = parseFloat(geometry?.x2 || '0');
      const y2 = parseFloat(geometry?.y2 || '0');

      const pixelDistance = calculatePixelDistance({ x: x1, y: y1 }, { x: x2, y: y2 });
      measurements.length = formatFeetAndInches(
        pixelsToFeet(pixelDistance, currentScale),
        precision
      );
      break;
    }

    case 'polyline': {
      const points = parsePoints(geometry?.points || '');
      measurements.length = formatFeetAndInches(
        calculatePolylineLength(points, currentScale),
        precision
      );
      break;
    }

    case 'polygon': {
      const points = parsePoints(geometry?.points || '');
      const perimeter = calculatePolygonPerimeter(points, currentScale);
      const area = calculatePolygonArea(points, currentScale);

      measurements.perimeter = formatFeetAndInches(perimeter, precision);
      measurements.area = formatArea(area, precision);
      break;
    }
  }

  return measurements;
};

/**
 * Parse points string into array of {x, y} objects
 * @param pointsString - String like "10,20 30,40 50,60"
 * @returns Array of point objects
 */
const parsePoints = (pointsString: string): Array<{ x: number; y: number }> => {
  if (!pointsString.trim()) return [];

  return pointsString
    .split(' ')
    .map(point => {
      const [x, y] = point.split(',').map(Number);
      return { x: x || 0, y: y || 0 };
    })
    .filter(point => !isNaN(point.x) && !isNaN(point.y));
};
